// WorldService (api.ts): cached world queries at the current date and leader.
//
// Every query reads the date and leader with `.value`, so a component calling it while rendering
// re-renders when either changes; actions read with `.peek()` and never subscribe.
//
//   victorySeries  27 yearly points × 6 tracks are ~150 victory scores: computed once per track
//   country        the report of one state at (date, leader): memoised until either changes
//   countryAtHex   the owner of a hex under the leader's view, as an iso3 (null for ocean, poles, …)

import type { DateStr, LeaderId } from '../core/schema.ts';
import { EPOCH_START, yearOf } from '../core/dates.ts';
import type { CountryReport, VictoryEntry, VictoryTrack } from '../world/api.ts';
import type { WorldImpl } from '../world/index.ts';
import type { WorldService } from './api.ts';
import type { Store } from './state.ts';

/** Countries whose reports are kept: a panel, a ribbon and a hover card ask again and again. */
const REPORTS_KEPT = 12;

/** Extras the UI feature-detects (src/ui/extensions.ts). */
export interface WorldExtras {
  landMask(): { land: Uint8Array; ice: Uint8Array };
  /** Monthly relation to `iso3` from 2000 up to the date shown. */
  relationHistory(iso3: string): ReturnType<WorldImpl['relationHistory']>;
  /** URL of the flag the state flew on the date shown, or null when it has none then. */
  flag(iso3: string): ReturnType<WorldImpl['flag']>;
  /** Rungs of the space ladder `iso3` has reached at the date shown, and the next one. */
  spaceLadder(iso3: string): ReturnType<WorldImpl['spaceLadder']>;
  controlSeries(warId: string): ReturnType<WorldImpl['controlSeries']>;
  /** The war as it stood on the date shown: nothing dated after it; null before it began. */
  warView(warId: string): ReturnType<WorldImpl['warView']>;
  /** Any actor by id (states, non-states, blocs), or null. */
  actorInfo(id: string): ReturnType<WorldImpl['actorInfo']>;
}

/** The point of each year's series: its last day, or the as-of date in the as-of year. */
export function seriesDates(asOf: DateStr): Array<{ year: number; date: DateStr }> {
  const out: Array<{ year: number; date: DateStr }> = [];
  for (let year = yearOf(EPOCH_START); year <= yearOf(asOf); year++) {
    out.push({ year, date: year === yearOf(asOf) ? asOf : `${year}-12-31` });
  }
  return out;
}

export function createWorldService(world: WorldImpl, store: Store): WorldService & WorldExtras {
  const series = new Map<VictoryTrack, Array<{ year: number; entries: VictoryEntry[] }>>();
  const reports = new Map<string, CountryReport>();

  /** The chosen leader, or the first perspective before one is chosen (the UI reads before the start screen closes). */
  function viewer(): LeaderId {
    return store.leader.value ?? world.perspectives[0].id;
  }

  function country(iso3: string): CountryReport {
    const date = store.date.value;
    const leader = viewer();
    const key = `${date}|${leader}|${iso3}`;
    const hit = reports.get(key);
    if (hit) {
      return hit;
    }

    const report = world.country(iso3, date, leader);
    if (reports.size >= REPORTS_KEPT) {
      reports.delete(reports.keys().next().value as string);
    }
    reports.set(key, report);
    return report;
  }

  function countryAtHex(hex: number): string | null {
    const leader = store.leader.value;
    if (!leader) {
      return null;
    }
    const index = world.snapshot(store.date.value, leader).owner[hex];
    const actor = index ? world.actorAt(index) : null;
    return actor && world.countries.some((c) => c.iso3 === actor.id) ? actor.id : null;
  }

  return {
    asOf: world.asOf,
    perspectives: () => world.perspectives,
    victory: (track) => world.victory(store.date.value, track),
    victorySeries(track) {
      const hit = series.get(track);
      if (hit) {
        return hit;
      }
      const made = seriesDates(world.asOf).map(({ year, date }) => ({ year, entries: world.victory(date, track) }));
      series.set(track, made);
      return made;
    },
    country,
    countryAtHex,
    hexInfo: (hex) => world.hexInfo(hex, store.date.value, viewer()),
    eventCounts: () => world.eventCounts(),
    events: (from, to) => world.events(from, to),
    source: (id) => world.source(id),
    lensIds: () => world.lensIds(),
    actor: (index) => world.actorAt(index),
    landMask: () => world.landMask(),
    relationHistory: (iso3) => world.relationHistory(iso3, viewer(), store.date.value),
    flag: (iso3) => world.flag(iso3, store.date.value),
    spaceLadder: (iso3) => world.spaceLadder(iso3, store.date.value),
    controlSeries: (warId) => world.controlSeries(warId, store.date.value),
    warView: (warId) => world.warView(warId, store.date.value),
    actorInfo: (id) => world.actorInfo(id),
  };
}
